import React, {useContext} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import {withRouter} from 'react-router';
import {AuthContext} from '../../../context/auth';
import {useLocation} from 'react-router-dom';
import {isLcTeacher} from '../../../utils/capabilities';
import WorkspaceNav from './WorkspaceNav.comp';
import { TEACHER_WORKSPACE_NAV } from '../../../config/workspaceNav/teacherWorkspace.nav';

const ORANGE = 'var(--brand-primary, #FF6900)';
const GREY = '#A5A5A5';

const useStyles = makeStyles(() => ({
    item: {
        width: '92%',
        margin: '2px 10px',
        borderRadius: '10px',
        padding: '10px 12px',
        color: GREY,
        '&:hover': {backgroundColor: 'rgba(237,102,40,0.08)'},
    },
    itemCollapsed: {
        width: '65%',
    },
    itemActive: {
        backgroundColor: ORANGE,
        color: '#ffffff',
        '&:hover': {backgroundColor: ORANGE},
    },
    /* Levels 2 and 3, styled as in the Partner and School sidebars. */
    subItem: {
        width: 'calc(100% - 34px)',
        margin: '1px 10px 1px 24px',
        borderRadius: '8px',
        padding: '7px 12px',
        color: GREY,
        '&:hover': {backgroundColor: 'rgba(237,102,40,0.08)'},
    },
    subItem2: {
        width: 'calc(100% - 48px)',
        margin: '1px 10px 1px 38px',
        borderRadius: '8px',
        padding: '5px 12px',
        color: GREY,
        '&:hover': {backgroundColor: 'rgba(237,102,40,0.08)'},
    },
    subItemActive: {
        backgroundColor: ORANGE,
        color: '#ffffff',
        '&:hover': {backgroundColor: ORANGE},
    },
    icon: {minWidth: 'unset', marginRight: '12px'},
    label: {
        '& .MuiTypography-root': {fontSize: '14px', fontWeight: 600, color: 'inherit'},
    },
    labelSub: {
        '& .MuiTypography-root': {fontSize: '13.5px', fontWeight: 500, color: 'inherit'},
    },
    labelSub2: {
        '& .MuiTypography-root': {fontSize: '13px', fontWeight: 400, color: 'inherit'},
    },
    divider: {margin: '4px 10px'},
}));

/**
 * Sidebar for a level-23 teacher — a strict subset with no Billing, Peers,
 * Client/School List or National Dashboard.
 *
 * Rendered from config via the shared WorkspaceNav, as Partner and School are.
 *
 * LearnCreator and the Program Impact Dashboard gate on the LearnCreator
 * CAPABILITY, not the level: a plain teacher (no capability) never sees either.
 * See docs/features/Teacher-Role-and-School-Workspace.md §4.
 */
const TeacherMainList = (props) => {
    const {history, isDrawerOpen} = props;
    const classes = useStyles();
    const location = useLocation();
    const {authUser} = useContext(AuthContext);

    const isActive = path => location.pathname === path || location.pathname.startsWith(path + '/');

    /** Everything the nav config's `visibleWhen` resolvers read. */
    const navCtx = {
        authUser,
        canAuthor: isLcTeacher(authUser),
    };

    /**
     * Solution gating is switched OFF for this workspace, deliberately.
     *
     * Every Teacher row in the sheet carries the identical tag "P1B, LC" —
     * because the header publishes the WHOLE workspace for those two solutions
     * ("Only Published for P1B and LC"). A uniform tag is a statement about the
     * workspace door, not about individual rows: applied per row it is
     * all-or-nothing, and "none" means a sidebar with no Home on it.
     *
     * Whether a teacher account exists at all is decided upstream — a school
     * without LearnCreator cannot grant the capability, and only LC Teachers can
     * sign in. Once someone is inside this workspace, every row applies to them.
     *
     * `resolveSolutions` is still imported and used by the other two sidebars;
     * passing null here is what tells WorkspaceNav not to gate.
     */
    const solutions = null;

    return (
        <div style={{display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden'}}>
            <List style={{flexGrow: 1, paddingTop: '8px', overflowY: 'auto'}}>
                {/*
                  * The whole menu comes from the PbDQ Navigation Bar sheet rows
                  * 152-168, via config rather than JSX.
                  */}
                <WorkspaceNav
                    items={TEACHER_WORKSPACE_NAV}
                    ctx={navCtx}
                    solutions={solutions}
                    classes={classes}
                    isDrawerOpen={isDrawerOpen}
                    currentPath={location.pathname}
                    isActive={isActive}
                    onNavigate={(path) => history.push(path)}
                />
            </List>
        </div>
    );
};

export default withRouter(TeacherMainList);
